import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRoleLabel } from '../../../utils/roleUtils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { MultiSelect } from '../../../components/ui/multi-select';
import { fetchWithAuth } from '../../../utils/fetchWithAuth';
import { Loader2, RefreshCw, Eye, EyeOff, Copy } from 'lucide-react';
import { getAllPermissions, getDefaultPermissions, usePermissionLabel } from '../../../constants/permissions';
import { generateRandomPassword } from '../../../utils/passwordUtils';

export const UserEditDialog = ({ open, user, onClose, onUserUpdated }) => {
  const { t } = useTranslation();
  const permissionLabel = usePermissionLabel();
  const formatRoleLabel = useRoleLabel();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [serviceDesks, setServiceDesks] = useState([]);
  const [selectedServiceDesks, setSelectedServiceDesks] = useState([]);
  const [selectedAllowedCreationDesks, setSelectedAllowedCreationDesks] = useState([]);

  // Every rule below is decided by the API and shipped with the row
  const abilities = user?.abilities ?? {};
  const assignableRoles = abilities.assignableRoles ?? [];
  const canEditRole = abilities.canChangeRole === true;
  const canEditServiceDesks = abilities.canEditServiceDesks === true;
  const canGrantPermissions = abilities.canGrantPermissions === true;
  const grantablePermissions = abilities.grantablePermissions ?? [];

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'user',
    mode: 'rw',
    lang: 'fr',
    serviceDeskId: '',
    sectors: '',
    permissions: [],
    allowedTicketCreationDesks: []
  });

  // Filter permission options to only show those not included in role defaults
  const defaultPermissionsForRole = getDefaultPermissions(formData.role);
  const availablePermissionOptions = getAllPermissions()
    .filter(option => !defaultPermissionsForRole.includes(option.value))
    // Never offer a grant the API would refuse, but keep what the target already holds visible
    .filter(option => grantablePermissions.includes(option.value) || formData.permissions.includes(option.value))
    .map(option => ({ value: option.value, label: permissionLabel(option.value) }));

  // Fetch service desks for superadmin
  useEffect(() => {
    if (open && canEditServiceDesks) {
      fetchServiceDesks();
    }
  }, [open, canEditServiceDesks]);

  const fetchServiceDesks = async () => {
    try {
      const response = await fetchWithAuth('/service-desks/all');
      const json = await response.json();
      if (json.success) {
        setServiceDesks(json.data);
      }
    } catch (err) {
      console.error('Error fetching service desks:', err);
    }
  };

  useEffect(() => {
    if (user) {
      const serviceDeskIdStr = Array.isArray(user.serviceDeskId)
        ? user.serviceDeskId.join(', ')
        : String(user.serviceDeskId || '');

      const allowedCreationDesks = user.allowed_ticket_creation_desks || [];

      setFormData({
        username: user.username || '',
        email: user.email || '',
        password: '', // Don't pre-fill password
        role: user.role || 'user',
        mode: user.mode || 'rw',
        lang: user.lang || 'fr',
        serviceDeskId: serviceDeskIdStr,
        sectors: Array.isArray(user.sectors) ? user.sectors.join(', ') : '',
        permissions: Array.isArray(user.extraPermissions) ? user.extraPermissions : [],
        allowedTicketCreationDesks: allowedCreationDesks
      });

      // Initialize service desk selections for superadmin
      if (canEditServiceDesks) {
        if (user.serviceDeskId === '*') {
          setSelectedServiceDesks(['*']);
        } else if (Array.isArray(user.serviceDeskId)) {
          setSelectedServiceDesks(user.serviceDeskId);
        } else if (user.serviceDeskId) {
          setSelectedServiceDesks([user.serviceDeskId]);
        } else {
          setSelectedServiceDesks([]);
        }

        // Initialize allowed creation desks for technicians
        setSelectedAllowedCreationDesks(allowedCreationDesks);
      }
    }
  }, [user, canEditServiceDesks]);

  // Convert service desks to MultiSelect options format
  const serviceDeskOptions = serviceDesks.map(sd => ({
    value: sd.id,
    label: sd.name
  }));

  // Handle multi-select change
  const handleServiceDesksChange = (selected) => {
    setSelectedServiceDesks(selected);

    // Update formData.serviceDeskId
    if (selected.includes('*')) {
      setFormData(prev => ({ ...prev, serviceDeskId: '*' }));
    } else if (selected.length > 0) {
      setFormData(prev => ({ ...prev, serviceDeskId: selected.join(', ') }));
    } else {
      setFormData(prev => ({ ...prev, serviceDeskId: '' }));
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);

    // Reset extra permissions and allowed creation desks when role changes
    if (field === 'role') {
      setFormData(prev => ({ ...prev, permissions: [], allowedTicketCreationDesks: [] }));
      setSelectedAllowedCreationDesks([]);
    }
  };

  const handlePermissionsChange = (selected) => {
    setFormData(prev => ({ ...prev, permissions: selected }));
  };

  const handleAllowedCreationDesksChange = (selected) => {
    setSelectedAllowedCreationDesks(selected);
    setFormData(prev => ({ ...prev, allowedTicketCreationDesks: selected }));
  };

  const handleGeneratePassword = () => {
    const password = generateRandomPassword();
    handleChange('password', password);
    setShowPassword(true);
  };

  const copyPasswordToClipboard = async () => {
    if (formData.password) {
      try {
        await navigator.clipboard.writeText(formData.password);
      } catch (err) {
        console.error('Failed to copy password:', err);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Parse sectors (comma-separated string to array)
      const sectorsArray = formData.sectors
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);

      // Parse serviceDeskId - always as array: [3], [3, 205], or ["*"]
      let serviceDeskId;
      const rawServiceDeskId = formData.serviceDeskId.trim();
      if (rawServiceDeskId === '*') {
        serviceDeskId = ['*'];
      } else if (rawServiceDeskId.includes(',')) {
        serviceDeskId = rawServiceDeskId.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
      } else {
        const parsed = parseInt(rawServiceDeskId);
        if (isNaN(parsed)) {
          throw new Error(t('Invalid Service Desk ID'));
        }
        serviceDeskId = [parsed];
      }

      const payload = {
        username: formData.username,
        email: formData.email,
        role: formData.role,
        mode: formData.mode,
        lang: formData.lang,
        serviceDeskId,
        sectors: sectorsArray,
        allowedTicketCreationDesks: formData.role === 'technician' ? formData.allowedTicketCreationDesks : []
      };

      // Only a super admin may grant permissions; omitting the field leaves them untouched
      if (canGrantPermissions) {
        payload.permissions = formData.permissions;
      }

      // Only include password if it's been set
      if (formData.password) {
        payload.password = formData.password;
      }

      const response = await fetchWithAuth(`/users/${user.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const json = await response.json();

      if (!response.ok) {
        throw new Error(json.error || t('Updating the user failed'));
      }

      if (json.success) {
        onUserUpdated();
      } else {
        throw new Error(json.error || t('Updating the user failed'));
      }
    } catch (err) {
      console.error('Error updating user:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>{t('Edit the user')}</DialogTitle>
          <DialogDescription>
            {t('Edit the information for')} {user.username}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="space-y-4 overflow-y-auto flex-1 pr-2">
            <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit-username">{t('Username *')}</Label>
              <Input
                id="edit-username"
                value={formData.username}
                onChange={(e) => handleChange('username', e.target.value)}
                autoComplete="off"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-email">{t('Email *')}</Label>
              <Input
                id="edit-email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                autoComplete="off"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-password">{t('New password')}</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  id="edit-password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  placeholder={t('Leave empty to keep unchanged')}
                  autoComplete="off"
                  className="pr-10"
                />
                {formData.password && (
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                )}
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={handleGeneratePassword}
                title={t('Generate a password')}
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
              {formData.password && (
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={copyPasswordToClipboard}
                  title={t('Copy the password')}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {t('Leave empty to keep unchanged, or generate a new secure password')}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {canEditRole && (
            <div className="space-y-2">
              <Label htmlFor="edit-role">{t('Role *')}</Label>
              <Select value={formData.role} onValueChange={(value) => handleChange('role', value)}>
                <SelectTrigger id="edit-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {/* The API lists exactly the roles this actor may assign */}
                  {assignableRoles.map((role) => (
                    <SelectItem key={role} value={role}>{formatRoleLabel(role)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="edit-mode">{t('Mode *')}</Label>
              <Select value={formData.mode} onValueChange={(value) => handleChange('mode', value)}>
                <SelectTrigger id="edit-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rw">{t('Read / Write')}</SelectItem>
                  <SelectItem value="r">{t('Read only')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-lang">{t('Language')}</Label>
            <Select value={formData.lang} onValueChange={(value) => handleChange('lang', value)}>
              <SelectTrigger id="edit-lang">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fr">🇫🇷 French</SelectItem>
                <SelectItem value="en">🇬🇧 English</SelectItem>
                <SelectItem value="es">🇪🇸 Spanish</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{t('Service Desk *')}</Label>
            {canEditServiceDesks ? (
              <>
                <MultiSelect
                  options={serviceDeskOptions}
                  selected={selectedServiceDesks}
                  onChange={handleServiceDesksChange}
                  placeholder={t('Select the Service Desks…')}
                  allOption={{ value: '*', label: t('All Service Desks') }}
                />
                <p className="text-xs text-muted-foreground">
                  {t('Select one or more Service Desks, or "All" to grant access to all')}
                </p>
              </>
            ) : (
              <>
                <Input
                  id="edit-serviceDeskId"
                  value={formData.serviceDeskId}
                  disabled
                  className="bg-gray-100"
                />
                <p className="text-xs text-muted-foreground">
                  {t('The Service Desk cannot be changed by an admin')}
                </p>
              </>
            )}
          </div>

          {formData.role === 'technician' && canEditServiceDesks && (
            <div className="space-y-2">
              <Label>{t('Allowed service desks for ticket creation')}</Label>
              <MultiSelect
                options={serviceDeskOptions}
                selected={selectedAllowedCreationDesks}
                onChange={handleAllowedCreationDesksChange}
                placeholder={t('Select the Service Desks…')}
              />
              <p className="text-xs text-muted-foreground">
                {t('Select the Service Desks on which this technician can create tickets')}
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="edit-sectors">{t('Sectors')}</Label>
            <Textarea
              id="edit-sectors"
              value={formData.sectors}
              onChange={(e) => handleChange('sectors', e.target.value)}
              placeholder={t('Ex: Sector A, Sector B, Sector C')}
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              {t('Enter the sectors separated by commas')}
            </p>
          </div>

          <div className="space-y-2">
            <Label>{t('Additional permissions')}</Label>
            {!canGrantPermissions ? (
              <p className="text-sm text-muted-foreground italic">
                {formData.permissions.length > 0
                  ? formData.permissions.map(permissionLabel).join(', ')
                  : t('none')}
                {' — '}
                {t('Only a super admin can change permissions')}
              </p>
            ) : availablePermissionOptions.length > 0 ? (
              <MultiSelect
                options={availablePermissionOptions}
                selected={formData.permissions}
                onChange={handlePermissionsChange}
                placeholder={t('Select additional permissions…')}
              />
            ) : (
              <p className="text-sm text-muted-foreground italic">
                {t('This role already has all available permissions.')}
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              {t('Default permissions for the role')} "{formData.role}" : {defaultPermissionsForRole.length > 0
                ? defaultPermissionsForRole.join(', ')
                : t('none')}
            </p>
          </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}
          </div>

          <DialogFooter className="flex-shrink-0 pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              {t('Cancel')}
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('Save the modifications')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
